import { ChevronDown, Clock, SearchX, X } from "lucide-react";
import { useTranslate } from "ra-core";
import type { ComponentType, CSSProperties, ReactNode } from "react";

import {
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";

import type { SearchResourceName } from "../types";
import type { RecentSearch } from "./recentSearches";
import { SEARCH_RESOURCES, getSnippet, highlight } from "./searchModel";
import { QUICK_LINKS, getDescriptor, recentValue } from "./searchLinks";
import {
  MIN_SEARCH_LENGTH,
  type LinkedResult,
  type SearchController,
} from "./useSearchController";

// Results shown per group until the user asks for more, so a busy group does
// not push the others out of view.
const GROUP_PREVIEW_SIZE = 3;
// A title-line excerpt must keep the match on screen, so less lead-in text.
const TITLE_SNIPPET_RADIUS = 25;
// Rows past this one appear together, so the stagger stays under ~150ms.
const MAX_STAGGER_INDEX = 8;

const staggerStyle = (index: number) =>
  ({ "--search-i": Math.min(index, MAX_STAGGER_INDEX) }) as CSSProperties;

const Highlighted = ({ text, terms }: { text: string; terms: string[] }) =>
  highlight(text, terms).map((part, index) =>
    part.isMatch ? (
      <mark key={index} className="search-mark">
        {part.text}
      </mark>
    ) : (
      part.text
    ),
  );

/** Rounded icon tile tinted per result type (see search.css). */
export const SearchTile = ({
  resource,
  icon: Icon,
}: {
  resource: SearchResourceName | "recent";
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
}) => (
  <span className="search-tile" data-resource={resource} aria-hidden>
    <Icon className="size-[18px]" strokeWidth={2} />
  </span>
);

const ResultRow = ({
  linked,
  terms,
  index,
  onOpen,
}: {
  linked: LinkedResult;
  terms: string[];
  index: number;
  onOpen: (linked: LinkedResult, title: string) => void;
}) => {
  const translate = useTranslate();
  const { result } = linked;
  const title = result.title || translate("crm.search.untitled_result");
  // A note's or task's title is the start of its text: when the match is
  // further in, show the text around it instead.
  const isTitleFromContent =
    !!result.content && result.content.startsWith(title);
  const snippet = result.content
    ? getSnippet(
        title,
        result.content,
        terms,
        isTitleFromContent ? TITLE_SNIPPET_RADIUS : undefined,
      )
    : null;
  const displayedTitle = isTitleFromContent && snippet ? snippet : title;
  const snippetLine = isTitleFromContent ? null : snippet;
  const descriptor = getDescriptor(result.resource);

  return (
    <CommandItem
      value={`${result.resource}.${result.record_id}`}
      onSelect={() => onOpen(linked, title)}
      className="search-row"
      style={staggerStyle(index)}
    >
      <SearchTile resource={result.resource} icon={descriptor.icon} />
      <span className="search-row__text">
        <span
          className={
            isTitleFromContent
              ? "search-row__title line-clamp-2"
              : "search-row__title truncate"
          }
        >
          <Highlighted text={displayedTitle} terms={terms} />
        </span>
        {result.subtitle || snippetLine ? (
          <span className="search-row__sub">
            {result.subtitle ? (
              <span className="shrink-0">{result.subtitle}</span>
            ) : null}
            {result.subtitle && snippetLine ? (
              <span aria-hidden className="shrink-0">
                ·
              </span>
            ) : null}
            {snippetLine ? (
              <span className="truncate">
                <Highlighted text={snippetLine} terms={terms} />
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
    </CommandItem>
  );
};

/** Results grouped by type, each group cut to a preview until expanded. */
export const SearchResultGroups = ({
  search,
}: {
  search: SearchController;
}) => {
  const translate = useTranslate();
  let index = 0;

  return SEARCH_RESOURCES.map(({ name, labelKey }) => {
    const groupResults = search.linkedResults.filter(
      ({ result }) => result.resource === name,
    );
    if (groupResults.length === 0) {
      return null;
    }
    const hiddenCount = search.expandedGroups.has(name)
      ? 0
      : Math.max(0, groupResults.length - GROUP_PREVIEW_SIZE);
    const shownResults = groupResults.slice(
      0,
      groupResults.length - hiddenCount,
    );
    return (
      <CommandGroup
        key={name}
        heading={translate(labelKey)}
        className="search-group"
      >
        {shownResults.map((linked) => (
          <ResultRow
            key={`${linked.result.resource}.${linked.result.record_id}`}
            linked={linked}
            terms={search.terms}
            index={index++}
            onOpen={search.openResult}
          />
        ))}
        {hiddenCount > 0 ? (
          <CommandItem
            value={`${name}.show_more`}
            onSelect={() => search.expandGroup(name)}
            className="search-row search-row--more"
          >
            <ChevronDown aria-hidden className="size-4" />
            <span>{translate("crm.search.show_more")}</span>
            <span className="search-row__count" aria-hidden>
              +{hiddenCount}
            </span>
          </CommandItem>
        ) : null}
      </CommandGroup>
    );
  });
};

const RecentRow = ({
  entry,
  index,
  search,
}: {
  entry: RecentSearch;
  index: number;
  search: SearchController;
}) => {
  const translate = useTranslate();
  if (entry.type === "query") {
    return (
      <CommandItem
        value={recentValue(entry)}
        onSelect={() => search.runQuery(entry.query)}
        className="search-row"
        style={staggerStyle(index)}
      >
        <SearchTile resource="recent" icon={Clock} />
        <span className="search-row__text">
          <span className="search-row__title truncate">{entry.query}</span>
        </span>
      </CommandItem>
    );
  }
  const descriptor = getDescriptor(entry.resource);
  return (
    <CommandItem
      value={recentValue(entry)}
      onSelect={() => search.openUrl(entry.url, entry)}
      className="search-row"
      style={staggerStyle(index)}
    >
      <SearchTile resource={entry.resource} icon={descriptor.icon} />
      <span className="search-row__text">
        <span className="search-row__title truncate">{entry.title}</span>
        <span className="search-row__sub">
          {translate(descriptor.labelKey)}
        </span>
      </span>
    </CommandItem>
  );
};

/** Recent searches with a "clear" action; nothing when there are none. */
export const RecentSearches = ({ search }: { search: SearchController }) => {
  const translate = useTranslate();
  const { recent, clear } = search.recents;
  if (recent.length === 0) return null;
  return (
    <div className="search-recent">
      <CommandGroup
        heading={translate("crm.search.recent")}
        className="search-group"
      >
        {recent.map((entry, index) => (
          <RecentRow
            key={entry.type === "query" ? `q.${entry.query}` : entry.url}
            entry={entry}
            index={index}
            search={search}
          />
        ))}
      </CommandGroup>
      <button
        type="button"
        className="search-recent__clear"
        onClick={clear}
        aria-label={translate("crm.search.clear_recent_label")}
      >
        {translate("crm.search.clear_recent")}
      </button>
    </div>
  );
};

/** Desktop quick links: keyboard-reachable rows of the palette. */
export const QuickLinkItems = ({ search }: { search: SearchController }) => {
  const translate = useTranslate();
  return (
    <>
      {search.recents.recent.length > 0 ? (
        <CommandSeparator className="search-separator" />
      ) : null}
      <CommandGroup
        heading={translate("crm.search.quick_links")}
        className="search-group"
      >
        {QUICK_LINKS.map(({ name, url }, index) => {
          const descriptor = getDescriptor(name);
          return (
            <CommandItem
              key={name}
              value={`quick.${name}`}
              onSelect={() => search.openUrl(url)}
              className="search-row"
              style={staggerStyle(index)}
            >
              <SearchTile resource={name} icon={descriptor.icon} />
              <span className="search-row__text">
                <span className="search-row__title">
                  {translate(descriptor.labelKey)}
                </span>
              </span>
            </CommandItem>
          );
        })}
      </CommandGroup>
    </>
  );
};

export const SearchHint = () => {
  const translate = useTranslate();
  return (
    <p className="search-hint">
      {translate("crm.search.hint", { min: MIN_SEARCH_LENGTH })}
    </p>
  );
};

export const NoResults = () => {
  const translate = useTranslate();
  return (
    <div className="search-state">
      <span className="search-state__icon" aria-hidden>
        <SearchX className="size-6" strokeWidth={1.75} />
      </span>
      <p className="search-state__title">{translate("crm.search.empty")}</p>
      <p className="search-state__text">{translate("crm.search.empty_hint")}</p>
    </div>
  );
};

export const SearchError = () => {
  const translate = useTranslate();
  return (
    <div role="alert" className="search-state search-state--error">
      <span className="search-state__icon" aria-hidden>
        <X className="size-6" strokeWidth={1.75} />
      </span>
      <p className="search-state__text">{translate("crm.search.error")}</p>
    </div>
  );
};

/** First query only: later ones keep the previous results on screen. */
export const SearchSkeleton = () => (
  <div className="search-skeleton" aria-hidden>
    {[0, 1, 2].map((row) => (
      <div key={row} className="search-skeleton__row">
        <span className="search-skeleton__tile" />
        <span className="search-skeleton__lines">
          <span />
          <span />
        </span>
      </div>
    ))}
  </div>
);

/**
 * Body of the list for the current state: empty state (no query), first
 * load, error, no result, or the grouped results.
 */
export const SearchBody = ({
  search,
  emptyState,
}: {
  search: SearchController;
  emptyState: ReactNode;
}) => {
  if (search.isTooShort) return emptyState;
  if (search.error) return <SearchError />;
  if (search.isPending) return <SearchSkeleton />;
  if (search.linkedResults.length === 0) return <NoResults />;
  return <SearchResultGroups search={search} />;
};
